import { useMemo, useState, useCallback, useEffect, useRef } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  fetchAssessmentById,
  fetchAssessmentEntries,
  fetchActivityObservations,
  saveActivityObservation,
  saveAssessmentEntry,
  updateAssessmentStatus,
  type SaveAssessmentEntryPayload,
  type SaveActivityObservationPayload
} from "@/services/repositories/assessment-repository";
import type {
  ActivityStepObservation,
  Assessment,
  AssessmentEntry,
  AssessmentStatus
} from "@/types/assessment";

export type ActivityAssessmentSaveStatus = "idle" | "saving" | "saved" | "error";
export type ObservationSaveStatus =
  | "idle"
  | "dirty"
  | "saving"
  | "saved"
  | "error"
  | "conflict";

export function useActivityAssessmentSession(
  requestedAssessmentId: string | undefined,
  expectedStudentId?: string,
): {
  assessment?: Assessment;
  assessmentLoading: boolean;
  assessmentError: unknown;
  entries: AssessmentEntry[];
  entriesLoading: boolean;
  observations: ActivityStepObservation[];
  observationsLoading: boolean;
  observationSaveStatus: Record<string, ObservationSaveStatus>;
  saveObservation: (
    payload: Omit<SaveActivityObservationPayload, "assessmentId" | "expectedVersion">,
  ) => void;
  retryObservation: (activityCode: string, stepId: string) => Promise<void>;
  reloadObservation: (activityCode: string, stepId: string) => Promise<void>;
  flushObservations: () => Promise<void>;
  saveEntry: (
    payload: Omit<SaveAssessmentEntryPayload, "assessmentId" | "expectedVersion">,
  ) => Promise<void>;
  flushEntries: () => Promise<void>;
  saveStatus: ActivityAssessmentSaveStatus;
  markStatus: (status: AssessmentStatus) => Promise<void>;
  marking: boolean;
} {
  const queryClient = useQueryClient();
  const [saveStatus, setSaveStatus] = useState<ActivityAssessmentSaveStatus>("idle");
  const saveQueues = useRef(new Map<string, Promise<void>>());
  const observationTimers = useRef(new Map<string, ReturnType<typeof setTimeout>>());
  const observationQueues = useRef(new Map<string, Promise<void>>());
  const observationLatest = useRef(
    new Map<string, Omit<SaveActivityObservationPayload, "assessmentId" | "expectedVersion">>(),
  );
  const observationRevisions = useRef(new Map<string, number>());
  const observationSavedRevisions = useRef(new Map<string, number>());
  const observationFlushAfterFlight = useRef(new Set<string>());
  const [observationSaveStatus, setObservationSaveStatus] = useState<
    Record<string, ObservationSaveStatus>
  >({});

  const assessmentQuery = useQuery({
    queryKey: ["assessments", requestedAssessmentId],
    enabled: Boolean(requestedAssessmentId),
    queryFn: () => fetchAssessmentById(requestedAssessmentId!),
    staleTime: Infinity
  });

  const assessmentId =
    assessmentQuery.data &&
    (!expectedStudentId || assessmentQuery.data.studentId === expectedStudentId)
      ? assessmentQuery.data.id
      : undefined;

  const entriesQuery = useQuery({
    queryKey: ["assessmentEntries", assessmentId],
    enabled: Boolean(assessmentId),
    queryFn: () =>
      assessmentId ? fetchAssessmentEntries(assessmentId) : Promise.resolve<AssessmentEntry[]>([]),
    staleTime: 30 * 1000
  });

  const observationsQuery = useQuery({
    queryKey: ["activityObservations", assessmentId],
    enabled: Boolean(assessmentId),
    queryFn: () =>
      assessmentId
        ? fetchActivityObservations(assessmentId)
        : Promise.resolve<ActivityStepObservation[]>([]),
    staleTime: 30 * 1000
  });

  const saveEntryMutation = useMutation({
    mutationFn: (payload: SaveAssessmentEntryPayload) => saveAssessmentEntry(payload),
    onMutate: () => setSaveStatus("saving"),
    onSuccess: (entry) => {
      queryClient.setQueryData<AssessmentEntry[] | undefined>(
        ["assessmentEntries", entry.assessmentId],
        (previous) => {
          const list = previous ? [...previous] : [];
          const index = list.findIndex((item) => item.entryCode === entry.entryCode);
          if (index >= 0) {
            list[index] = entry;
          } else {
            list.push(entry);
          }
          return list;
        },
      );
      setSaveStatus("saved");
    },
    onError: () => setSaveStatus("error"),
    onSettled: () => {
      setTimeout(() => setSaveStatus("idle"), 1500);
    }
  });

  const markStatusMutation = useMutation({
    mutationFn: (status: AssessmentStatus) => {
      if (!assessmentId) {
        return Promise.resolve(undefined);
      }
      return updateAssessmentStatus(assessmentId, status);
    },
    onSuccess: async (updated) => {
      if (updated) {
        queryClient.setQueryData(["assessments", updated.id], updated);
        await queryClient.invalidateQueries({ queryKey: ["assessments"] });
      }
    }
  });

  const entries = useMemo(() => entriesQuery.data ?? [], [entriesQuery.data]);
  const observations = useMemo(
    () => observationsQuery.data ?? [],
    [observationsQuery.data],
  );

  const setObservationStatus = useCallback((key: string, status: ObservationSaveStatus) => {
    setObservationSaveStatus((previous) => ({ ...previous, [key]: status }));
  }, []);

  const runObservationSave = useCallback(
    async (key: string): Promise<void> => {
      const timer = observationTimers.current.get(key);
      if (timer) {
        clearTimeout(timer);
        observationTimers.current.delete(key);
      }
      if (!assessmentId) return;
      const existing = observationQueues.current.get(key);
      if (existing) {
        observationFlushAfterFlight.current.add(key);
        return existing;
      }
      const payload = observationLatest.current.get(key);
      if (!payload) return;
      const savingRevision = observationRevisions.current.get(key) ?? 0;
      const cached =
        queryClient.getQueryData<ActivityStepObservation[]>([
          "activityObservations",
          assessmentId
        ]) ?? [];
      const expectedVersion =
        cached.find(
          (item) =>
            item.activityCode === payload.activityCode && item.stepId === payload.stepId,
        )?.version ?? 0;
      setObservationStatus(key, "saving");
      const request = saveActivityObservation({ assessmentId, ...payload, expectedVersion })
        .then((saved) => {
          queryClient.setQueryData<ActivityStepObservation[] | undefined>(
            ["activityObservations", assessmentId],
            (previous) => {
              const list = previous ? [...previous] : [];
              const index = list.findIndex(
                (item) =>
                  item.activityCode === saved.activityCode && item.stepId === saved.stepId,
              );
              if (index >= 0) list[index] = saved;
              else list.push(saved);
              return list;
            },
          );
          observationSavedRevisions.current.set(key, savingRevision);
          setObservationStatus(
            key,
            (observationRevisions.current.get(key) ?? 0) === savingRevision
              ? "saved"
              : "dirty",
          );
        })
        .catch((error: unknown) => {
          setObservationStatus(
            key,
            isVersionConflict(error) ? "conflict" : "error",
          );
          throw error;
        })
        .finally(() => {
          observationQueues.current.delete(key);
          if (
            observationFlushAfterFlight.current.delete(key) &&
            (observationRevisions.current.get(key) ?? 0) >
              (observationSavedRevisions.current.get(key) ?? 0)
          ) {
            void runObservationSave(key).catch(() => undefined);
          }
        });
      observationQueues.current.set(key, request);
      return request;
    },
    [assessmentId, queryClient, setObservationStatus],
  );

  const saveObservation = useCallback(
    (payload: Omit<SaveActivityObservationPayload, "assessmentId" | "expectedVersion">) => {
      const key = observationKey(payload.activityCode, payload.stepId);
      observationLatest.current.set(key, payload);
      observationRevisions.current.set(key, (observationRevisions.current.get(key) ?? 0) + 1);
      setObservationStatus(key, "dirty");
      const existingTimer = observationTimers.current.get(key);
      if (existingTimer) clearTimeout(existingTimer);
      observationTimers.current.set(
        key,
        setTimeout(() => {
          observationTimers.current.delete(key);
          void runObservationSave(key).catch(() => undefined);
        }, 800),
      );
    },
    [runObservationSave, setObservationStatus],
  );

  const retryObservation = useCallback(
    async (activityCode: string, stepId: string) => {
      await runObservationSave(observationKey(activityCode, stepId));
    },
    [runObservationSave],
  );

  const reloadObservation = useCallback(
    async (activityCode: string, stepId: string) => {
      const key = observationKey(activityCode, stepId);
      const timer = observationTimers.current.get(key);
      if (timer) {
        clearTimeout(timer);
        observationTimers.current.delete(key);
      }
      const result = await observationsQuery.refetch();
      if (result.error) throw result.error;
      observationLatest.current.delete(key);
      observationSavedRevisions.current.set(
        key,
        observationRevisions.current.get(key) ?? 0,
      );
      observationFlushAfterFlight.current.delete(key);
      setObservationStatus(key, "idle");
    },
    [observationsQuery, setObservationStatus],
  );

  const flushObservations = useCallback(async () => {
    let keys: string[];
    do {
      keys = [...observationLatest.current.keys()].filter(
        (key) =>
          (observationRevisions.current.get(key) ?? 0) >
          (observationSavedRevisions.current.get(key) ?? 0),
      );
      if (keys.length > 0) {
        await Promise.all(keys.map((key) => runObservationSave(key)));
      }
    } while (keys.length > 0);
  }, [runObservationSave]);

  useEffect(() => {
    const handleBeforeUnload = (event: BeforeUnloadEvent) => {
      const hasUnsaved = [...observationRevisions.current.keys()].some(
        (key) =>
          (observationRevisions.current.get(key) ?? 0) >
          (observationSavedRevisions.current.get(key) ?? 0),
      );
      if (hasUnsaved) {
        event.preventDefault();
        event.returnValue = "";
      }
    };
    window.addEventListener("beforeunload", handleBeforeUnload);
    return () => window.removeEventListener("beforeunload", handleBeforeUnload);
  }, []);

  useEffect(
    () => () => {
      observationTimers.current.forEach((timer) => clearTimeout(timer));
      observationTimers.current.clear();
    },
    [],
  );

  const saveEntry = useCallback(
    (payload: Omit<SaveAssessmentEntryPayload, "assessmentId" | "expectedVersion">) => {
      if (!assessmentId) {
        return Promise.reject(new Error("评估记录尚未就绪"));
      }
      const previous = saveQueues.current.get(payload.entryCode) ?? Promise.resolve();
      const next = previous
        .catch(() => undefined)
        .then(async () => {
          const cached =
            queryClient.getQueryData<AssessmentEntry[]>(["assessmentEntries", assessmentId]) ?? [];
          const current = cached.find((entry) => entry.entryCode === payload.entryCode);
          const expectedVersion = current?.version ?? 0;
          await saveEntryMutation.mutateAsync({
            assessmentId,
            ...payload,
            score: payload.score === undefined ? (current?.score ?? null) : payload.score,
            comment: payload.comment === undefined ? (current?.comment ?? "") : payload.comment,
            expectedVersion
          });
        });
      saveQueues.current.set(payload.entryCode, next);
      const cleanup = () => {
        if (saveQueues.current.get(payload.entryCode) === next) {
          saveQueues.current.delete(payload.entryCode);
        }
      };
      void next.then(cleanup, cleanup);
      return next;
    },
    [assessmentId, queryClient, saveEntryMutation],
  );

  const flushEntries = useCallback(async () => {
    await Promise.all([...saveQueues.current.values()]);
  }, []);

  const markStatus = useCallback(
    async (status: AssessmentStatus) => {
      await flushObservations();
      await flushEntries();
      await markStatusMutation.mutateAsync(status);
    },
    [flushEntries, flushObservations, markStatusMutation],
  );

  return {
    assessment: assessmentQuery.data,
    assessmentLoading: assessmentQuery.isLoading,
    assessmentError: assessmentQuery.error,
    entries,
    entriesLoading: entriesQuery.isLoading,
    observations,
    observationsLoading: observationsQuery.isLoading,
    observationSaveStatus,
    saveObservation,
    retryObservation,
    reloadObservation,
    flushObservations,
    saveEntry,
    flushEntries,
    saveStatus,
    markStatus,
    marking: markStatusMutation.isPending
  };
}

function observationKey(activityCode: string, stepId: string): string {
  return `${activityCode}:${stepId}`;
}

function isVersionConflict(error: unknown): boolean {
  return (
    typeof error === "object" &&
    error !== null &&
    "status" in error &&
    (error as { status?: number }).status === 409
  );
}
